import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException, DecodeHintType, BarcodeFormat, ResultPoint } from '@zxing/library';
import { getLogger } from '@packages/core/logging';
import { Typography } from '@packages/ui/shared';

const logger = getLogger('vin-sessions');

type QRCodeScannerProps = {
    onSuccessScanCallback: (scannedData: string) => void;
    onErrorScanCallback: (errorMessage: string) => void;
    scannedDataValidator: (qrCodeDecoded: string) => boolean;
    remoteQRCodeValidator: (scannedData: string) => Promise<boolean>;
};

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
    onSuccessScanCallback,
    onErrorScanCallback,
    scannedDataValidator,
    remoteQRCodeValidator,
}) => {
    const defaultErrMessage = "Please Scan the vehicle's battery pack QR code";
    const invalidErrMessage = 'QR code is invalid, please scan the correct Code';

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const viewfinderRef = useRef<HTMLDivElement | null>(null);
    const readerRef = useRef<BrowserMultiFormatReader | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const [isScanning, setIsScanning] = useState(false);
    const [isRemoteValidating, setIsRemoteValidating] = useState(false);
    const [borderColor, setBorderColor] = useState('white');
    const [errorMsg, setErrorMsg] = useState(defaultErrMessage);

    const capturedRef = useRef(false);
    const lastCapturedTextRef = useRef<string>('');

    const resetTimerRef = useRef<number | null>(null);
    const holdActiveRef = useRef<boolean>(false);

    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);

    const stopScan = useCallback(() => {
        setIsScanning(false);
        try {
            (readerRef.current as any)?._controls?.stop();
        } catch {}
        const el = videoRef.current;
        const stream = (el?.srcObject as MediaStream) || streamRef.current;
        if (stream && typeof stream.getTracks === 'function') {
            stream.getTracks().forEach((t) => t.stop());
        }
        if (el) el.srcObject = null;
        streamRef.current = null;
    }, []);

    const autoFreezeUi = useCallback(() => {
        setBorderColor('#2ecc71'); // green
        stopScan();
    }, [stopScan]);

    // UPDATED: make border red and show red error on remote invalid
    const flashRemoteInvalid = useCallback(() => {
        if (resetTimerRef.current) {
            clearTimeout(resetTimerRef.current);
            resetTimerRef.current = null;
        }

        holdActiveRef.current = true;
        setBorderColor('#e74c3c'); // red corners
        setErrorMsg(invalidErrMessage); // red text

        resetTimerRef.current = window.setTimeout(() => {
            holdActiveRef.current = false;
            setBorderColor('white');
            setErrorMsg(defaultErrMessage);
            resetTimerRef.current = null;
        }, 5000);
    }, []);

    const initiateRemoteValidation = useCallback(
        async (text: string) => {
            if (isRemoteValidating) return;
            capturedRef.current = true;
            setIsRemoteValidating(true);

            try {
                const ok = await remoteQRCodeValidator(text);
                if (ok) {
                    setIsRemoteValidating(false);
                    autoFreezeUi();
                    onSuccessScanCallback(text);
                } else {
                    setIsRemoteValidating(false);
                    capturedRef.current = false;
                    lastCapturedTextRef.current = '';
                    flashRemoteInvalid();
                    onErrorScanCallback(invalidErrMessage);
                }
            } catch (e) {
                logger.error('Failed to validate the QR code remotely', { error: e });
                setIsRemoteValidating(false);
                capturedRef.current = false;
                lastCapturedTextRef.current = '';
                flashRemoteInvalid();
                onErrorScanCallback(invalidErrMessage);
            }
        },
        [
            remoteQRCodeValidator,
            isRemoteValidating,
            autoFreezeUi,
            onSuccessScanCallback,
            onErrorScanCallback,
            flashRemoteInvalid,
        ]
    );

    const startScan = useCallback(async () => {
        if (isScanning) return;

        setBorderColor('white');
        setErrorMsg(defaultErrMessage);
        capturedRef.current = false;

        const constraints: MediaStreamConstraints = {
            audio: false,
            video: {
                facingMode: { ideal: 'environment' },
                width: { ideal: 1920, min: 1280 },
                height: { ideal: 1080, min: 720 },
                frameRate: { ideal: 30, max: 60 },
                ...({ advanced: [{ focusMode: 'continuous' }, { exposureMode: 'continuous' }] } as any),
            },
        };

        try {
            const reader = new BrowserMultiFormatReader(hints, {
                delayBetweenScanAttempts: 150,
                delayBetweenScanSuccess: 150,
            });
            readerRef.current = reader;

            await reader.decodeFromConstraints(constraints, videoRef.current!, (res, err, controls) => {
                if (controls && (readerRef.current as any) && !(readerRef.current as any)._controls) {
                    (readerRef.current as any)._controls = controls;
                }

                const v = videoRef.current;
                if (v?.srcObject && !streamRef.current) {
                    streamRef.current = v.srcObject as MediaStream;
                }

                if (isRemoteValidating || holdActiveRef.current) return;

                let nextColor = 'white';

                if (res) {
                    const pts = (res.getResultPoints?.() as ResultPoint[] | null) || [];
                    if (pts.length && videoRef.current && viewfinderRef.current) {
                        const inside = pointsInsideViewfinder(pts, videoRef.current, viewfinderRef.current);
                        if (inside) {
                            const text = res.getText();
                            const isValid = scannedDataValidator(text);

                            if (!capturedRef.current || text !== lastCapturedTextRef.current) {
                                if (isValid) {
                                    nextColor = '#2ecc71'; // green
                                    setBorderColor(nextColor);
                                    lastCapturedTextRef.current = text;
                                    setErrorMsg('');
                                    initiateRemoteValidation(text);
                                } else {
                                    // UPDATED: local invalid -> make corners red + red message
                                    nextColor = '#e74c3c';
                                    setBorderColor(nextColor);
                                    setErrorMsg(invalidErrMessage);
                                }
                            }
                        } else {
                            nextColor = 'white';
                            setErrorMsg(defaultErrMessage);
                        }
                    }
                } else {
                    if (err instanceof NotFoundException) {
                        setErrorMsg(defaultErrMessage);
                        nextColor = 'white';
                    } else if (err && (err as any)?.name !== 'NotFoundException') {
                        logger.trace('Failed to decode the QR code from the camera stream', { error: err });
                        setErrorMsg(defaultErrMessage);
                        nextColor = 'white';
                    }
                }

                setBorderColor((prev) => (prev === nextColor ? prev : nextColor));
            });

            setIsScanning(true);
        } catch (e) {
            logger.error('Failed to start the camera for the QR code scanner', { error: e });
            const msg =
                (e && (e as Error).message) ||
                'Camera access failed. Use HTTPS (or http://localhost) and allow camera permission.';
            setErrorMsg(msg);
            onErrorScanCallback(msg);
            setBorderColor('white');
            setIsScanning(false);
        }
    }, [isScanning, hints, scannedDataValidator, initiateRemoteValidation, isRemoteValidating, onErrorScanCallback]);

    useEffect(() => {
        (async () => {
            await startScan().catch(() => {});
        })();
        return () => {
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
                resetTimerRef.current = null;
            }
            holdActiveRef.current = false;
            stopScan();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ===== Styles =====
    const containerStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        maxWidth: 800,
        height: 540,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        aspectRatio: '3 / 4',
        minHeight: 360,
    };
    const videoStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        background: 'white',
        padding: 24,
        visibility: isRemoteValidating ? 'hidden' : 'visible',
    };
    const overlayStyle: React.CSSProperties = { position: 'absolute', inset: 0, pointerEvents: 'none' };
    const viewfinderStyle: React.CSSProperties = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        height: 188,
        width: 212,
        transform: 'translate(-50%, -50%)',
        border: `1px solid gray`,
        visibility: isRemoteValidating ? 'hidden' : 'visible',
    };
    const loaderBackdropStyle: React.CSSProperties = {
        position: 'absolute',
        inset: 0,
        background: 'rgba(255,255,255,0.9)',
        display: isRemoteValidating ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3,
    };
    const spinnerStyle: React.CSSProperties = {
        width: 56,
        height: 56,
        borderRadius: '50%',
        border: '6px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0d6efd',
        animation: 'qrspin 0.8s linear infinite',
    };

    return (
        <div>
            <style>{`@keyframes qrspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <div style={containerStyle}>
                <video ref={videoRef} style={videoStyle} autoPlay muted playsInline />

                <div style={loaderBackdropStyle} data-testid="remote-validating-overlay">
                    <div style={{ textAlign: 'center', color: '#0d6efd' }}>
                        <div style={spinnerStyle} />
                        <div style={{ marginTop: 12 }}>
                            <Typography variant="label" color="blueOneActive">
                                Validating…
                            </Typography>
                        </div>
                    </div>
                </div>

                <div style={overlayStyle}>
                    <div ref={viewfinderRef} style={viewfinderStyle}>
                        {['tl', 'tr', 'br', 'bl'].map((corner) => {
                            const styleMap: Record<string, React.CSSProperties> = {
                                tl: { top: -8, left: -8, borderWidth: '8px 0 0 8px' },
                                tr: { top: -8, right: -8, borderWidth: '8px 8px 0 0' },
                                br: { bottom: -8, right: -8, borderWidth: '0 8px 8px 0' },
                                bl: { bottom: -8, left: -8, borderWidth: '0 0 8px 8px' },
                            };
                            return (
                                <div
                                    key={corner}
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        position: 'absolute',
                                        borderStyle: 'solid',
                                        borderColor: borderColor,
                                        ...styleMap[corner],
                                    }}
                                />
                            );
                        })}
                        <div style={{ position: 'absolute', bottom: '-60px' }}>
                            <Typography
                                variant="label"
                                style={{ width: '100%', display: 'flex', textAlign: 'center' }}
                                color={errorMsg === invalidErrMessage ? 'redOne' : 'white'}
                            >
                                {errorMsg}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QRCodeScanner;

function pointsInsideViewfinder(
    points: ResultPoint[],
    videoEl: HTMLVideoElement,
    viewfinderEl: HTMLDivElement
): boolean {
    const videoRect = videoEl.getBoundingClientRect();
    const vfRect = viewfinderEl.getBoundingClientRect();

    const sourceW = videoEl.videoWidth || videoRect.width;
    const sourceH = videoEl.videoHeight || videoRect.height;

    const scale = Math.max(videoRect.width / sourceW, videoRect.height / sourceH);
    const displayW = sourceW * scale;
    const displayH = sourceH * scale;
    const offsetX = videoRect.left + (videoRect.width - displayW) / 2;
    const offsetY = videoRect.top + (videoRect.height - displayH) / 2;

    return points.every((p) => {
        const clientX = offsetX + p.getX() * scale;
        const clientY = offsetY + p.getY() * scale;
        return clientX >= vfRect.left && clientX <= vfRect.right && clientY >= vfRect.top && clientY <= vfRect.bottom;
    });
}
